import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

const UserAnalytics = ({ contract, setError, setLoading, loading }) => {
  const [users, setUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalDeposited, setTotalDeposited] = useState('0');
  const [totalWithdrawn, setTotalWithdrawn] = useState('0');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'lastActivity',
    direction: 'descending'
  });

  useEffect(() => {
    if (contract) {
      loadUserData();
    }
  }, [contract]);

  const loadUserData = async () => {
    setIsLoadingData(true);
    setLoading(true);
    try {
      // Get event logs for different user interactions
      const depositFilter = contract.filters.Deposit();
      const sharesMintedFilter = contract.filters.SharesMinted();
      const withdrawDepositFilter = contract.filters.DepositWithdrawn();
      const withdrawTokensFilter = contract.filters.TokensWithdrawnFromFund();
      const redeemTokensFilter = contract.filters.IndexTokensRedeemed();

      // Query all relevant events (go back about 10000 blocks to limit query size)
      const depositEvents = await contract.queryFilter(depositFilter, -10000, 'latest');
      const mintEvents = await contract.queryFilter(sharesMintedFilter, -10000, 'latest');
      const withdrawDepositEvents = await contract.queryFilter(withdrawDepositFilter, -10000, 'latest');
      const withdrawTokenEvents = await contract.queryFilter(withdrawTokensFilter, -10000, 'latest');
      const redeemEvents = await contract.queryFilter(redeemTokensFilter, -10000, 'latest');

      // Track unique users and their activities
      const userMap = new Map();
      
      // Process deposit events
      depositEvents.forEach(event => {
        const user = event.args.user;
        const amount = ethers.utils.formatEther(event.args.amount);
        const timestamp = new Date(event.blockNumber * 15000); // Approximate timestamp
        
        if (!userMap.has(user)) {
          userMap.set(user, {
            address: user,
            depositCount: 0,
            mintCount: 0,
            withdrawCount: 0,
            redeemCount: 0,
            totalDeposited: 0,
            totalWithdrawn: 0,
            firstActivity: timestamp,
            lastActivity: timestamp,
            currentFundTokens: 0,
            currentDeposit: 0
          });
        }
        
        const userData = userMap.get(user);
        userData.depositCount++;
        userData.totalDeposited += parseFloat(amount);
        userData.lastActivity = timestamp > userData.lastActivity ? timestamp : userData.lastActivity;
        
        // Update the map
        userMap.set(user, userData);
      });
      
      // Process mint events
      mintEvents.forEach(event => {
        const user = event.args.user;
        const amount = ethers.utils.formatEther(event.args.amount);
        const timestamp = new Date(event.blockNumber * 15000);
        
        if (!userMap.has(user)) {
          userMap.set(user, {
            address: user,
            depositCount: 0,
            mintCount: 0,
            withdrawCount: 0,
            redeemCount: 0,
            totalDeposited: 0,
            totalWithdrawn: 0,
            firstActivity: timestamp,
            lastActivity: timestamp,
            currentFundTokens: 0,
            currentDeposit: 0
          });
        }
        
        const userData = userMap.get(user);
        userData.mintCount++;
        userData.lastActivity = timestamp > userData.lastActivity ? timestamp : userData.lastActivity;
        
        // Update the map
        userMap.set(user, userData);
      });
      
      // Process withdraw events
      withdrawDepositEvents.forEach(event => {
        const user = event.args.user;
        const amount = ethers.utils.formatEther(event.args.amount);
        const timestamp = new Date(event.blockNumber * 15000);
        
        if (!userMap.has(user)) {
          userMap.set(user, {
            address: user,
            depositCount: 0,
            mintCount: 0,
            withdrawCount: 0,
            redeemCount: 0,
            totalDeposited: 0,
            totalWithdrawn: 0,
            firstActivity: timestamp,
            lastActivity: timestamp,
            currentFundTokens: 0,
            currentDeposit: 0
          });
        }
        
        const userData = userMap.get(user);
        userData.withdrawCount++;
        userData.totalWithdrawn += parseFloat(amount);
        userData.lastActivity = timestamp > userData.lastActivity ? timestamp : userData.lastActivity;
        
        // Update the map
        userMap.set(user, userData);
      });
      
      // Process redeem events
      redeemEvents.forEach(event => {
        const user = event.args.user;
        const tokensRedeemed = ethers.utils.formatEther(event.args.tokensRedeemed);
        const timestamp = new Date(event.blockNumber * 15000);
        
        if (!userMap.has(user)) {
          userMap.set(user, {
            address: user,
            depositCount: 0,
            mintCount: 0,
            withdrawCount: 0,
            redeemCount: 0,
            totalDeposited: 0,
            totalWithdrawn: 0,
            firstActivity: timestamp,
            lastActivity: timestamp,
            currentFundTokens: 0,
            currentDeposit: 0
          });
        }
        
        const userData = userMap.get(user);
        userData.redeemCount++;
        userData.lastActivity = timestamp > userData.lastActivity ? timestamp : userData.lastActivity;
        
        // Update the map
        userMap.set(user, userData);
      });
      
      // Fetch current balances for all users
      const usersArray = Array.from(userMap.keys());
      let totalDeposits = 0;
      let totalWithdrawn = 0;
      
      // Get current balances for all users
      for (const userAddress of usersArray) {
        try {
          const fundTokens = await contract.userFundTokens(userAddress);
          const depositBalance = await contract.userDeposits(userAddress);
          const userData = userMap.get(userAddress);
          
          userData.currentFundTokens = parseFloat(ethers.utils.formatEther(fundTokens));
          userData.currentDeposit = parseFloat(ethers.utils.formatEther(depositBalance));
          
          totalDeposits += userData.totalDeposited;
          totalWithdrawn += userData.totalWithdrawn;
          
          userMap.set(userAddress, userData);
        } catch (error) {
          console.error(`Error fetching data for user ${userAddress}:`, error);
        }
      }
      
      // Convert Map to array for rendering
      const usersData = Array.from(userMap.values());
      
      // Sort users by default
      const sortedUsers = sortUserData(usersData, sortConfig.key, sortConfig.direction);
      
      // Update state with processed data
      setUsers(sortedUsers);
      setTotalDeposited(totalDeposits.toFixed(4));
      setTotalWithdrawn(totalWithdrawn.toFixed(4));
      setTotalUsers(usersData.length);
      setActiveUsers(usersData.filter(user => user.currentFundTokens > 0 || user.currentDeposit > 0).length);
      
    } catch (error) {
      console.error("Error loading user data:", error);
      setError("Failed to load user data: " + error.message);
    } finally {
      setIsLoadingData(false);
      setLoading(false);
    }
  };

  const sortUserData = (userData, key, direction) => {
    const sortedData = [...userData];
    
    sortedData.sort((a, b) => {
      // For date fields
      if (key === 'firstActivity' || key === 'lastActivity') {
        const dateA = new Date(a[key]).getTime();
        const dateB = new Date(b[key]).getTime();
        return direction === 'ascending' ? dateA - dateB : dateB - dateA;
      }
      
      // For numeric fields
      if (typeof a[key] === 'number') {
        return direction === 'ascending' ? a[key] - b[key] : b[key] - a[key];
      }
      
      // For string fields
      return direction === 'ascending' 
        ? a[key].toString().localeCompare(b[key].toString())
        : b[key].toString().localeCompare(a[key].toString());
    });
    
    return sortedData;
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    
    setSortConfig({ key, direction });
    setUsers(sortUserData(users, key, direction));
  };

  const formatAddress = (address) => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  const refreshData = () => {
    loadUserData();
  };

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">User Analytics</h2>
        <button
          className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700"
          onClick={refreshData}
          disabled={loading || isLoadingData}
        >
          {(loading || isLoadingData) ? "Loading..." : "Refresh Data"}
        </button>
      </div>
      
      {/* Analytics Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-blue-600">Total Users</div>
          <div className="text-2xl font-bold">{totalUsers}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm text-green-600">Active Users</div>
          <div className="text-2xl font-bold">{activeUsers}</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-sm text-purple-600">Total Deposited</div>
          <div className="text-2xl font-bold">{totalDeposited} ETH</div>
        </div>
        <div className="bg-amber-50 p-4 rounded-lg">
          <div className="text-sm text-amber-600">Total Withdrawn</div>
          <div className="text-2xl font-bold">{totalWithdrawn} ETH</div>
        </div>
      </div>
      
      {/* User Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort('address')}
              >
                User Address
              </th>
              <th 
                className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort('currentFundTokens')}
              >
                Fund Tokens
              </th>
              <th 
                className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort('currentDeposit')}
              >
                ETH Deposit
              </th>
              <th 
                className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort('depositCount')}
              >
                Deposits
              </th>
              <th 
                className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort('mintCount')}
              >
                Mints
              </th>
              <th 
                className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort('totalDeposited')}
              >
                Total Deposited
              </th>
              <th 
                className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort('lastActivity')}
              >
                Last Activity
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.length > 0 ? (
              users.map((user, index) => (
                <tr key={index} className={user.currentFundTokens > 0 || user.currentDeposit > 0 ? 'bg-green-50' : ''}>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatAddress(user.address)}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-right">
                    {user.currentFundTokens.toFixed(4)}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-right">
                    {user.currentDeposit.toFixed(4)}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-right">
                    {user.depositCount}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-right">
                    {user.mintCount}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-right">
                    {user.totalDeposited.toFixed(4)} ETH
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                    {user.lastActivity.toLocaleDateString()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-3 py-4 text-center text-sm text-gray-500">
                  {isLoadingData ? "Loading user data..." : "No users found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Explainer */}
      <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-400 text-blue-700">
        <h4 className="font-bold">About User Analytics</h4>
        <ul className="list-disc ml-5 mt-1 text-sm">
          <li className="mb-1">Shows all users who have interacted with the fund</li>
          <li className="mb-1">Active users (highlighted) have current holdings or deposits</li>
          <li className="mb-1">Click column headers to sort the table</li>
          <li>This data is gathered from blockchain events and may take time to load</li>
        </ul>
      </div>
    </div>
  );
};

export default UserAnalytics;